import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import SectionBackground from "@/components/layout/SectionBackground";
import GhostCursor from "@/components/animations/GhostCursor";

const Teams = () => {
  const navigate = useNavigate();
  const heroRef = useRef<HTMLElement>(null);

  // Scroll-based zoom effect
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  // Zoom in effect - scales from 1 to 1.5
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.5]);
  
  // Title zoom effect - more pronounced zoom
  const titleScale = useTransform(scrollYProgress, [0, 1], [1, 2]);
  
  // Fade out as zooming in
  const opacity = useTransform(scrollYProgress, [0, 0.8, 1], [1, 0.3, 0]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.7, 1], [1, 0.2, 0]);

  // Blur effect for depth
  const blur = useTransform(scrollYProgress, [0, 1], [0, 10]);
  const filter = useTransform(blur, (value) => `blur(${value}px)`);

  return (
    <main className="relative min-h-screen">
      <SectionBackground />

      {/* 🟠 HERO SECTION - With its own ghost cursor */}
      <div className="relative">
        {/* Ghost Cursor Effect - Hero Section Only - Original Specifications */}
        <GhostCursor
          color="#E67E22"
          brightness={1.2}
          mixBlendMode="screen"
          trailLength={60}
          inertia={0.4}
          bloomStrength={0.15}
          bloomRadius={1.2}
          grainIntensity={0.03}
          zIndex={5}
        />

      {/* 🟠 HERO SECTION */}
      <motion.section 
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ paddingTop: '88px' }}
      >

        {/* Background with zoom effect */}
        <motion.div
          className="absolute inset-0 z-0"
          style={{
            scale,
            opacity,
            filter,
            backgroundColor: "#000000",
          }}
        />

        {/* Exclusive Spots Badge - Top Right */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="absolute top-24 md:top-28 right-4 md:right-8 z-20"
        >
          <div
            className="backdrop-blur-sm rounded-full px-4 py-1.5 md:px-6 md:py-2"
            style={{
              background: "linear-gradient(135deg, #D4AF37 0%, #F4E4C1 50%, #C9A961 100%)",
              boxShadow: "0 0 25px rgba(212, 175, 55, 0.5), 0 0 50px rgba(212, 175, 55, 0.25), 0 4px 15px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
              border: "1px solid rgba(244, 228, 193, 0.4)",
            }}
          >
            <span 
              className="text-xs font-bold uppercase tracking-wide"
              style={{
                fontFamily: "'Grift', sans-serif",
                background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
              }}
            >
              Exclusive Spots – One Team per city
            </span>
          </div>
        </motion.div>

        {/* Hero Content - Centered */}
        <div className="relative z-10 max-w-6xl mx-auto px-8 md:px-12 lg:px-16 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mb-6 leading-tight"
            style={{
              fontFamily: "'Grift', sans-serif",
              fontSize: "clamp(32px, 6vw, 72px)",
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: "#FFFFFF",
              textShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
              fontStyle: "italic",
              scale: titleScale,
              opacity: titleOpacity,
            }}
          >
            <motion.span
              style={{
                display: "inline-block",
                transform: "skewX(-5deg)",
              }}
            >
              <span style={{ color: "#FFFFFF" }}>Official</span>{" "}
              <span 
                className="inline-block relative"
                style={{ color: "#E67E22" }}
              >
                Teams
                {/* Animated Underline */}
                <motion.div
                  className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                  style={{ backgroundColor: "#E67E22" }}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: 0.9 }}
                />
              </span>
            </motion.span>
          </motion.h1>

          {/* Subtitle - Serif */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mb-8 max-w-2xl"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(16px, 2vw, 22px)",
              color: "rgba(255, 255, 255, 0.9)",
              letterSpacing: "0.02em",
              fontWeight: 400,
              lineHeight: 1.6,
              fontStyle: "italic",
            }}
          >
            Join the international network of Klau & Ros Teams. Apply to represent your city.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            {/* Primary CTA */}
            <button
              onClick={() => navigate("/apply")}
              className="px-8 py-4 rounded-full font-bold text-base md:text-lg transition-all duration-300 flex items-center gap-2 group"
              style={{
                background: "linear-gradient(90deg, #E67E22 0%, #FF9E4F 100%)",
                color: "#FFFFFF",
                fontFamily: "'Grift', sans-serif",
                fontStyle: "italic",
                transform: "skewX(-5deg)",
                boxShadow: "0 4px 20px rgba(230, 126, 34, 0.4)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "skewX(-5deg) translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 6px 30px rgba(230, 126, 34, 0.6)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "skewX(-5deg) translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 20px rgba(230, 126, 34, 0.4)";
              }}
            >
              <span>Apply to Become a Team Leader</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>

            {/* Secondary CTA */}
            <button
              onClick={() => navigate("/courses")}
              className="px-8 py-4 rounded-full font-bold text-base md:text-lg transition-all duration-300 flex items-center gap-2 group"
              style={{
                background: "transparent",
                color: "#FFFFFF",
                fontFamily: "'Grift', sans-serif",
                fontStyle: "italic",
                transform: "skewX(-5deg)",
                border: "2px solid rgba(255, 255, 255, 0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "skewX(-5deg) translateY(-2px)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.5)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "skewX(-5deg) translateY(0)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.3)";
              }}
            >
              <span>View Courses</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </motion.section>
      </div>

      {/* ⚫ CONTENT SECTIONS - With its own ghost cursor */}
      <div className="relative">
        {/* Ghost Cursor Effect - Content Sections Only */}
        <GhostCursor
          color="#E67E22"
          brightness={0.3}
          mixBlendMode="screen"
          trailLength={20}
          inertia={0.6}
          bloomStrength={0.02}
          bloomRadius={0.5}
          grainIntensity={0.005}
          zIndex={5}
        />

      {/* ⚫ SECTION 1 — WHAT ARE THE OFFICIAL TEAMS? */}
      <section className="relative z-10 py-20 md:py-32 px-8 md:px-12 lg:px-16">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-left"
          >
            <h2
              className="mb-8 md:mb-12 leading-tight"
              style={{
                fontFamily: "'Grift', sans-serif",
                fontSize: "clamp(32px, 6vw, 72px)",
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                color: "#FFFFFF",
                textShadow: "0 2px 20px rgba(0, 0, 0, 0.5)",
                fontStyle: "italic",
                transform: "skewX(-5deg)",
              }}
            >
              What Are the{" "}
              <span 
                className="inline-block relative"
                style={{ color: "#E67E22" }}
              >
                Official Teams
                <motion.div
                  className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                  style={{ backgroundColor: "#E67E22" }}
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                />
              </span>
              ?
            </h2>
            <div
              className="rounded-3xl p-8 md:p-12 leading-relaxed space-y-6"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(16px, 2vw, 20px)",
                color: "rgba(255, 255, 255, 0.9)",
                background: "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                boxShadow: "0 8px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
                fontStyle: "italic",
              }}
            >
              <p className="text-xl md:text-2xl" style={{ fontStyle: "normal", marginBottom: "1.5rem" }}>
                A global movement of dancers representing Klau & Ros around the world.
              </p>
              <div className="space-y-5">
                <p>
                  The Klau & Ros Teams are one-year official programs that allow dance schools or leaders to represent our choreographies in their city.
                </p>
                <p>
                  Each team receives exclusive material, mentorship, and formal recognition from Klau and Ros.
                </p>
                <p>
                  Because of our exclusivity system, every city can only have one official team.
                </p>
                <p>
                  Applications are reviewed manually to ensure no overlap or conflicts between nearby teams.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ⚫ SECTION 2 — TEAM OPTIONS */}
      <section className="relative z-10 py-20 md:py-32 px-8 md:px-12 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16 md:mb-20 leading-tight text-left"
            style={{
              fontFamily: "'Grift', sans-serif",
              fontSize: "clamp(32px, 6vw, 72px)",
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: "#FFFFFF",
              textShadow: "0 2px 20px rgba(0, 0, 0, 0.5)",
              fontStyle: "italic",
              transform: "skewX(-5deg)",
            }}
          >
            <span 
              className="inline-block relative"
              style={{ color: "#E67E22" }}
            >
              Team Options
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                style={{ backgroundColor: "#E67E22" }}
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </span>
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-8 md:gap-10">
            {/* Team Option 1 */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="rounded-3xl p-8 md:p-10 transition-all duration-300"
              style={{
                background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                boxShadow: "0 8px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-8px)";
                e.currentTarget.style.borderColor = "rgba(230, 126, 34, 0.4)";
                e.currentTarget.style.boxShadow = "0 12px 60px rgba(230, 126, 34, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
                e.currentTarget.style.boxShadow = "0 8px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15)";
              }}
            >
              <div className="mb-6">
                <h3
                  className="text-xl md:text-2xl font-bold mb-2"
                  style={{
                    fontFamily: "'Grift', sans-serif",
                    color: "#E67E22",
                    fontStyle: "italic",
                    transform: "skewX(-5deg)",
                  }}
                >
                  1️⃣ KLAU & ROS BOOTCAMP TEAM – "VUELA"
                </h3>
              </div>

              <ul className="space-y-4 mb-8">
                {[
                  "1-year renewable license",
                  "Exclusivity radius: 250–500 km",
                  "Direct feedback from Klau & Ros",
                  "Official certificate",
                  "Access to community #teams on the website",
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div
                      className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-1"
                      style={{ background: "#22C55E" }}
                    >
                      <Check size={14} color="white" strokeWidth={3} />
                    </div>
                    <span
                      className="text-base md:text-lg"
                      style={{
                        fontFamily: "'Playfair Display', serif",
                        color: "rgba(255, 255, 255, 0.9)",
                        fontStyle: "italic",
                      }}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="pt-6 border-t border-white/10">
                <p
                  className="text-2xl md:text-3xl font-bold"
                  style={{
                    fontFamily: "'Grift', sans-serif",
                    color: "#E67E22",
                    fontStyle: "italic",
                    transform: "skewX(-5deg)",
                  }}
                >
                  Price: €650
                </p>
                <p
                  className="text-sm mt-1"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    color: "rgba(255, 255, 255, 0.7)",
                    fontStyle: "italic",
                  }}
                >
                  one-time payment
                </p>
              </div>
        </motion.div>

            {/* Team Option 2 */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="rounded-3xl p-8 md:p-10 transition-all duration-300"
              style={{
                background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                boxShadow: "0 8px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-8px)";
                e.currentTarget.style.borderColor = "rgba(230, 126, 34, 0.4)";
                e.currentTarget.style.boxShadow = "0 12px 60px rgba(230, 126, 34, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
                e.currentTarget.style.boxShadow = "0 8px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15)";
              }}
            >
              <div className="mb-6">
                <h3
                  className="text-xl md:text-2xl font-bold mb-2"
                  style={{
                    fontFamily: "'Grift', sans-serif",
                    color: "#E67E22",
                    fontStyle: "italic",
                    transform: "skewX(-5deg)",
                  }}
                >
                  2️⃣ KLAU'S LADY STYLE TEAM
                </h3>
              </div>

              <ul className="space-y-4 mb-8">
                {[
                  "1-year official license",
                  "Focus on styling, elegance, and performance",
                  "Exclusive choreography materials",
                  "Access to community #teams on the website",
                  "Official costume: Felinas x Klau bodysuit (€60–€80)",
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div
                      className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-1"
                      style={{ background: "#22C55E" }}
                    >
                      <Check size={14} color="white" strokeWidth={3} />
                    </div>
                    <span
                      style={{
                        fontFamily: "'Grift', sans-serif",
                        color: "rgba(255, 255, 255, 0.8)",
                      }}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="pt-6 border-t border-white/10">
                <p
                  className="text-2xl md:text-3xl font-bold"
                  style={{
                    fontFamily: "'Grift', sans-serif",
                    color: "#E67E22",
                    fontStyle: "italic",
                    transform: "skewX(-5deg)",
                  }}
                >
                  Price: €500
                </p>
                <p
                  className="text-sm mt-1"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    color: "rgba(255, 255, 255, 0.7)",
                    fontStyle: "italic",
                  }}
                >
                  one-time payment
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ⚫ SECTION 3 — HOW IT WORKS */}
      <section className="relative z-10 py-20 md:py-32 px-8 md:px-12 lg:px-16">
        <div className="max-w-5xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16 md:mb-20 leading-tight text-left"
            style={{
              fontFamily: "'Grift', sans-serif",
              fontSize: "clamp(32px, 6vw, 72px)",
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: "#FFFFFF",
              textShadow: "0 2px 20px rgba(0, 0, 0, 0.5)",
              fontStyle: "italic",
              transform: "skewX(-5deg)",
            }}
          >
            <span 
              className="inline-block relative"
              style={{ color: "#E67E22" }}
            >
              How It Works
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                style={{ backgroundColor: "#E67E22" }}
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </span>
          </motion.h2>

          <div className="space-y-8">
            {[
              {
                step: "1️⃣",
                title: "Submit Your Request",
                description: "Fill out the form and select your city.",
              },
              {
                step: "2️⃣",
                title: "City Availability Check",
                description: "We confirm whether your city or region is available.",
              },
              {
                step: "3️⃣",
                title: "Approval & Payment",
                description: "Upon approval, you'll receive an invoice in your profile for your official Team license.",
              },
              {
                step: "4️⃣",
                title: "Receive Materials",
                description: "Access choreography videos, music, and the leader community.",
              },
              {
                step: "5️⃣",
                title: "Start Training",
                description: "Launch your official Team and begin your journey.",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex gap-6 md:gap-8 items-start p-6 md:p-8 rounded-2xl transition-all duration-300"
                style={{
                  background: "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)",
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(10px)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateX(8px)";
                  e.currentTarget.style.borderColor = "rgba(230, 126, 34, 0.3)";
                  e.currentTarget.style.background = "linear-gradient(135deg, rgba(230, 126, 34, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateX(0)";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.15)";
                  e.currentTarget.style.background = "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)";
                }}
              >
                <div
                  className="text-4xl flex-shrink-0"
                  style={{
                    fontFamily: "serif",
                  }}
                >
                  {item.step}
                </div>
                <div>
                  <h3
                    className="text-xl md:text-2xl font-bold mb-2"
                    style={{
                      fontFamily: "'Grift', sans-serif",
                      color: "#E67E22",
                      fontStyle: "italic",
                      transform: "skewX(-5deg)",
                    }}
                  >
                    {item.title}
                  </h3>
                  <p
                    className="text-base md:text-lg"
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      color: "rgba(255, 255, 255, 0.9)",
                      fontStyle: "italic",
                    }}
                  >
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ⚫ SECTION 4 — TERMS OF EXCLUSIVITY */}
      <section className="relative z-10 py-20 md:py-32 px-8 md:px-12 lg:px-16">
        <div className="max-w-5xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16 md:mb-20 leading-tight text-left"
            style={{
              fontFamily: "'Grift', sans-serif",
              fontSize: "clamp(32px, 6vw, 72px)",
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: "#FFFFFF",
              textShadow: "0 2px 20px rgba(0, 0, 0, 0.5)",
              fontStyle: "italic",
              transform: "skewX(-5deg)",
            }}
          >
            Terms of{" "}
            <span 
              className="inline-block relative"
              style={{ color: "#E67E22" }}
            >
              Exclusivity
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                style={{ backgroundColor: "#E67E22" }}
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="rounded-3xl p-8 md:p-12"
            style={{
              background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 8px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
            }}
          >
            <ul className="space-y-4">
              {[
                "Each Team license is valid for 1 year.",
                "Only one team per city is allowed.",
                "Exclusivity radius: 250–500 km, depending on location.",
                "All applications are manually reviewed by the NEXTGEN Management Team.",
                "Annual renewal required to maintain representation rights.",
                "Inactive or non-renewed teams lose exclusivity; their city becomes available again.",
              ].map((term, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div
                    className="flex-shrink-0 w-2 h-2 rounded-full mt-2"
                    style={{ background: "#E67E22" }}
                  />
                  <span
                    className="text-base md:text-lg"
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      color: "rgba(255, 255, 255, 0.9)",
                      fontStyle: "italic",
                    }}
                  >
                    {term}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* 🧡 SECTION 5 — BENEFITS */}
      <section className="relative z-10 py-20 md:py-32 px-8 md:px-12 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-left mb-16 md:mb-20"
          >
            <h2
              className="mb-4 md:mb-6 leading-tight"
              style={{
                fontFamily: "'Grift', sans-serif",
                fontSize: "clamp(32px, 6vw, 72px)",
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                color: "#E67E22",
                textShadow: "0 2px 20px rgba(230, 126, 34, 0.4)",
                fontStyle: "italic",
                transform: "skewX(-5deg)",
              }}
            >
              <span 
                className="inline-block relative"
              >
                Benefits
                <motion.div
                  className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                  style={{ backgroundColor: "#E67E22" }}
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                />
              </span>
            </h2>
            <p
              className="text-lg md:text-xl"
              style={{
                fontFamily: "'Playfair Display', serif",
                color: "rgba(255, 255, 255, 0.9)",
                fontStyle: "italic",
              }}
            >
              Why apply for an official Team?
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[
              "Official representation of Klau & Ros in your city",
              "Direct mentorship and performance feedback",
              "Access to exclusive choreography materials",
              "International promotion on website and social media",
              "Official certificate from Klau & Ros + Endless Academy",
              "Access to private Team Leader community",
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-start gap-4 p-6 rounded-2xl transition-all duration-300"
                style={{
                  background: "linear-gradient(135deg, rgba(230, 126, 34, 0.15) 0%, rgba(255, 158, 79, 0.08) 100%)",
                  border: "1px solid rgba(230, 126, 34, 0.3)",
                  backdropFilter: "blur(10px)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.borderColor = "rgba(230, 126, 34, 0.5)";
                  e.currentTarget.style.boxShadow = "0 8px 30px rgba(230, 126, 34, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.borderColor = "rgba(230, 126, 34, 0.3)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5"
                  style={{ 
                    background: "linear-gradient(135deg, #E67E22 0%, #FF9E4F 100%)",
                    boxShadow: "0 0 15px rgba(230, 126, 34, 0.5)",
                  }}
                >
                  <Check size={18} color="white" strokeWidth={3} />
                </div>
                <span
                  className="text-base md:text-lg"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    color: "rgba(255, 255, 255, 0.9)",
                    lineHeight: "1.6",
                    fontStyle: "italic",
                  }}
                >
                  {benefit}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ⚫ SECTION 6 — FINAL CTA */}
      <section className="relative z-10 py-20 md:py-32 px-8 md:px-12 lg:px-16">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="rounded-3xl p-12 md:p-16 text-left"
            style={{
              background: "linear-gradient(135deg, rgba(230, 126, 34, 0.15) 0%, rgba(255, 158, 79, 0.08) 100%)",
              backdropFilter: "blur(20px)",
              border: "2px solid rgba(230, 126, 34, 0.3)",
              boxShadow: "0 8px 60px rgba(230, 126, 34, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
            }}
          >
            <h2
              className="mb-6 md:mb-8 leading-tight"
              style={{
                fontFamily: "'Grift', sans-serif",
                fontSize: "clamp(32px, 6vw, 72px)",
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                color: "#FFFFFF",
                textShadow: "0 2px 20px rgba(0, 0, 0, 0.5)",
                fontStyle: "italic",
                transform: "skewX(-5deg)",
              }}
            >
              Ready to Lead Your{" "}
              <span 
                className="inline-block relative"
                style={{ color: "#E67E22" }}
              >
                Own Team
                <motion.div
                  className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                  style={{ backgroundColor: "#E67E22" }}
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                />
              </span>
              ?
            </h2>
            <p
              className="text-lg md:text-xl mb-10 md:mb-12"
              style={{
                fontFamily: "'Playfair Display', serif",
                color: "rgba(255, 255, 255, 0.9)",
                fontStyle: "italic",
              }}
            >
              Apply today to represent Klau & Ros in your city.
            </p>
            <button
              onClick={() => navigate("/apply")}
              className="px-10 py-5 rounded-full font-bold text-base md:text-lg transition-all duration-300 flex items-center gap-2 group"
              style={{
                background: "linear-gradient(90deg, #E67E22 0%, #FF9E4F 100%)",
                color: "#FFFFFF",
                fontFamily: "'Grift', sans-serif",
                fontStyle: "italic",
                transform: "skewX(-5deg)",
                boxShadow: "0 4px 20px rgba(230, 126, 34, 0.4)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "skewX(-5deg) translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 6px 30px rgba(230, 126, 34, 0.6)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "skewX(-5deg) translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 20px rgba(230, 126, 34, 0.4)";
              }}
            >
              <span>Apply Now</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </section>
      </div>
    </main>
  );
};

export default Teams;
